import type { IUserRepository } from "@src/domain/users/repository/user.repository.interface";
import type { UserDTO } from "../dto/user.dto";
import { UserDtoMapper } from "../mappers/user-dto.mapper";

/**
 * Use Case: Upload Profile Picture
 * Uploads a profile picture for the authenticated user
 */
export class UploadProfilePictureUseCase {
    constructor(private readonly userRepository: IUserRepository) { }

    /**
     * Execute the use case
     * @param file - Image file to upload
     * @param onUploadProgress - Optional callback for upload progress
     * @returns Promise<UserDTO> - Updated user DTO with new profile picture
     * @throws Error if validation fails
     */
    async execute(file: File, onUploadProgress?: (progress: number) => void): Promise<UserDTO> {
        this.validateFile(file);

        const user = await this.userRepository.uploadProfilePicture(file, onUploadProgress);
        return UserDtoMapper.toDTO(user);
    }

    /**
     * Validate file
     * @param file - The file to validate
     * @throws Error if validation fails
     */
    private validateFile(file: File): void {
        if (!file) {
            throw new Error('File is required');
        }

        // Check file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            throw new Error('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed');
        }

        // Check file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            throw new Error('File size must be less than 5MB');
        }
    }
}
